import { useObserver } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { usePropertyContactInfoStore } from "..";
import IProperty from "../interfaces/IProperty";
import IContactInfo from "../interfaces/IContactInfo";
import ContactInfoApiService from "../services/contactInfoApiService";

export default function usePropertyContactInfo(property: IProperty | undefined): IPropertyContactInfo {
    const contactInfoStore = usePropertyContactInfoStore();
    const contactInfoApiService = useMemo(() => new ContactInfoApiService(), []);
    const [isFetchCalled, setIsFetchCalled] = useState(false);

    useEffect(() => setIsFetchCalled(false), [property]);

    const fetchContactInfo = useCallback(() => {
        if (!property || contactInfoStore.getIsLoadingFor(property.id) || contactInfoStore.getContactInfoFor(property.id))
            return;

        contactInfoStore.setIsLoadingFor(property.id, true);
        contactInfoStore.setIsErrorFor(property.id, false);
        setIsFetchCalled(true);
    }, [contactInfoStore, property]);

    const fetchInternal = async () => {
        if (!property) return;

        try {
            const contactInfo = await contactInfoApiService.fetchContactInfo(property);
            contactInfoStore.setContactInfoFor(property.id, contactInfo);
        } catch (error) {
            toast.error(`Failed to load contact information: ${error}`);
            contactInfoStore.setIsErrorFor(property.id, true);
        } finally {
            contactInfoStore.setIsLoadingFor(property.id, false);
        }
    };

    useEffect(() => {
        if (property && isFetchCalled) {
            setIsFetchCalled(false);
            fetchInternal();
        }
    }, [property, isFetchCalled]);

    return useObserver(() => ({
        contactInfo: property ? contactInfoStore.getContactInfoFor(property.id) : undefined,
        isLoading: contactInfoStore.getIsLoadingFor(property?.id),
        isError: contactInfoStore.getIsErrorFor(property?.id),
        fetchContactInfo,
    }));
}

interface IPropertyContactInfo {
    contactInfo?: IContactInfo;
    isLoading: boolean;
    isError: boolean;
    fetchContactInfo: () => void;
}
